import { BackendRoute } from '../../config/index';
import { QueryKey } from '../../constants/index';
import { HttpException } from '../../exceptions/index';
import { ApiService } from '../../services/index';
import {
  CreateEventDto,
  CreateEventResponse,
  EventLifecycleStatus,
  EventUnionWithPermissions,
} from '../../types/events.types';
import { useIdempotentAppMutation } from '../use-idempotent-app-mutation.hook';
import { useInvalidateQueries } from '../use-invalidate-queries.hook';
import { pendingEventsStore, PendingMutationSettleOutcome } from './pending-events.store';

type CreateEventVars = {
  companyId: string;
  data: CreateEventDto;
  // Fires once when the queued EVENT_MUTATION apply settles (the reserved id lands in the
  // polled list, or the TTL elapses), not when the POST resolves — the create toast belongs
  // here so it agrees with the skeleton row leaving the list.
  onApplySettled?: (outcome: PendingMutationSettleOutcome) => void;
};
type CreateContext = { tempId: string };

const buildPlaceholderEvent = (tempId: string, data: CreateEventDto): EventUnionWithPermissions => {
  const now = new Date();
  // Minimal placeholder: the row renders as a skeleton (short-circuited on lifecycleStatus), so
  // only identity + type + lifecycleStatus are read. Cast because a full EventUnion is not known
  // until the backend creates the real event.
  return {
    additionalNotes: '',
    canDelete: false,
    canEdit: false,
    createdAt: now,
    date: now,
    eventId: tempId,
    filesLinks: [],
    id: tempId,
    lifecycleStatus: EventLifecycleStatus.PENDING_CREATE,
    pinned: false,
    type: data.type,
    updatedAt: now,
  } as unknown as EventUnionWithPermissions;
};

export const useCreate = () => {
  const { invalidateQuery } = useInvalidateQueries(
    QueryKey.GET_EVENTS,
    QueryKey.GET_EVENT,
    QueryKey.GET_EVENT_TRANSACTIONS,
    QueryKey.GET_EVENT_TRANSACTIONS_COUNT,
    QueryKey.GET_ISSUED_SHARES,
    QueryKey.GET_SNAPSHOTS,
    QueryKey.GET_SNAPSHOT,
    QueryKey.GET_CURRENT_VALUATION,
    QueryKey.GET_COMPANY_INCORPORATION_CONTEXT,
    QueryKey.GET_COMPANY_GET_STARTED_PROGRESS,
    QueryKey.GET_STAKEHOLDER_REVERSE_VESTING,
  );

  const {
    isPending,
    mutate: create,
    mutateAsync: createAsync,
    data,
  } = useIdempotentAppMutation<CreateEventResponse, HttpException, CreateEventVars, CreateContext>(
    [QueryKey.CREATE_EVENT],
    {
      mutationFn: ({ companyId, data: body }, { headers }) =>
        ApiService.post(
          { endpoint: BackendRoute.COMPANIES, routePath: [companyId, 'events'] },
          { body: body, headers },
        ),
      // Show a client-side skeleton immediately; the overlay in useEvents keeps it across refetches
      // until the real event (reservedId) appears or the entry ages out.
      onMutate: ({ companyId, data: body, onApplySettled }) => {
        const tempId = crypto.randomUUID();
        pendingEventsStore.getState().addPendingCreate({
          companyId,
          createdAtMs: Date.now(),
          event: buildPlaceholderEvent(tempId, body),
          onApplySettled,
          tempId,
        });
        return { tempId };
      },
      onSuccess: (response, _variables, context) => {
        if (context?.tempId && response?.id) {
          pendingEventsStore.getState().setReservedId(context.tempId, response.id);
        }
        invalidateQuery();
      },
      onError: (_error, _variables, context) => {
        if (context?.tempId) {
          pendingEventsStore.getState().removePendingCreate(context.tempId);
        }
      },
    },
  );

  return { create, createAsync, isPending, data };
};
